import { useSession } from '@emerald-works/react-auth'
import { Bookmark, CollectionsBookmark } from '@mui/icons-material'
import { Box } from '@mui/material'
import PropTypes from 'prop-types'
import React, { useCallback } from 'react'
import { useNavigate } from 'react-router'
import {
  AddToPlaylistButton,
  BookmarkButton,
  LikeButton
} from '../../../../components/action-buttons'
import { useAddToBookmarks } from '../../../../components/add-to-bookmarks'
import { BaseContentCard } from '../../../../components/general-card'
import { useCardExpand } from '../../../../components/general-card/hooks'
import { useLike } from '../../../../components/like'
import { contentUrl } from '../../../../helpers/url'
import { useDisplayAddToPlaylist } from '../../../../hooks/useDisplayAddToPlaylist'
import { useStyles } from './style'

/**
 * Category Landing Content Card - Adapter component that uses HomeContentCard styling
 * but maintains the category landing page interface and behavior
 */
export const CategoryContentCard = ({
  content,
  onSelect,
  onBookmark,
  bookmarked,
  actionLoading,
  onAddToPlaylist,
  dataTest,
  classes: externalClasses,
  ...props
}) => {
  const navigate = useNavigate()
  const { isAuthenticated } = useSession()
  const { isBookmarked, handleBookmark, bookmarking } = useAddToBookmarks({
    from: 'category-landing'
  })
  const { isLiked, handleLike, liking } = useLike({ from: 'category-landing' })
  const displayAddToPlaylist = useDisplayAddToPlaylist()
  const classes = useStyles()

  const { expanded, handleMouseEnter, handleMouseLeave } = useCardExpand({
    expand: props.expand !== undefined ? props.expand : true
  })

  const handleCardSelect = useCallback(() => {
    if (onSelect) {
      onSelect()
    } else if (content) {
      navigate(contentUrl(content, 'category'))
    }
  }, [onSelect, content, navigate])

  const handleCardBookmark = useCallback(() => {
    if (onBookmark) {
      onBookmark()
    } else if (content) {
      handleBookmark(content)
    }
  }, [onBookmark, content, handleBookmark])

  const handleCardLike = useCallback(() => {
    if (content) {
      handleLike(content)
    }
  }, [content, handleLike])

  const handleAddToPlaylist = useCallback(() => {
    if (onAddToPlaylist) {
      if (typeof onAddToPlaylist === 'function') {
        onAddToPlaylist(content)
      } else {
        onAddToPlaylist(content)
      }
    }
  }, [onAddToPlaylist, content])

  const actions = {
    leftActions: (
      <Box
        display='flex'
        flexDirection='row'
        gap={1}
        justifyContent='flex-start'
        alignItems='center'
      >
        <LikeButton
          size='xs'
          label=''
          hasLiked={isLiked(content || {})}
          loading={!isAuthenticated ? false : liking(content || {})}
          hideText
          onClick={handleCardLike}
          aria-label='Like this item'
        />
        <BookmarkButton
          size='xs'
          variant={
            (bookmarked !== undefined ? bookmarked : isBookmarked(content))
              ? 'secondary'
              : 'secondary-noselect'
          }
          onClick={handleCardBookmark}
          loading={
            !isAuthenticated
              ? false
              : actionLoading !== undefined
                ? actionLoading
                : bookmarking(content)
          }
          hideText
          startIcon={<Bookmark />}
          bookmarked={
            bookmarked !== undefined ? bookmarked : isBookmarked(content)
          }
          aria-label='Bookmark this item'
        />
        {displayAddToPlaylist && (
          <AddToPlaylistButton
            size='xs'
            variant='secondary-noselect'
            startIcon={<CollectionsBookmark />}
            onClick={handleAddToPlaylist}
            aria-label='Add this item to a playlist'
          />
        )}
      </Box>
    )
  }

  return (
    <BaseContentCard
      content={content}
      actions={actions}
      expand={expanded}
      hoverShadow={false}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onSelect={handleCardSelect}
      testId={dataTest}
      overrides={{
        actions: classes.actions,
        summary: classes.summary,
        title: classes.title,
        subtitle: classes.subtitle,
        root: externalClasses?.root || classes.root,
        card: classes.card
      }}
      {...props}
    />
  )
}

CategoryContentCard.propTypes = {
  /** Content to be displayed on card */
  content: PropTypes.object,
  /** Callback function fired when the card is selected */
  onSelect: PropTypes.func,
  /** Callback function fired when bookmark button is clicked */
  onBookmark: PropTypes.func,
  /** Whether the content is bookmarked */
  bookmarked: PropTypes.bool,
  /** Whether bookmark action is loading */
  actionLoading: PropTypes.bool,
  /** Function to show add to playlist dialog */
  onAddToPlaylist: PropTypes.func,
  /** Test ID for the card */
  dataTest: PropTypes.string,
  /** Should the card always be expanded (show description)? */
  expand: PropTypes.bool,
  /** External CSS classes to override default styling */
  classes: PropTypes.object
}

CategoryContentCard.defaultProps = {
  size: 'small',
  expand: true
}

export default CategoryContentCard
